import { createContext, useContext, useId, type AriaAttributes, type ReactNode } from 'react';
import { AlertCircle } from 'lucide-react';
import { cn } from '@/lib/cn';

interface FieldContextValue {
  id: string;
  hintId: string;
  errorId: string;
  invalid: boolean;
  required: boolean;
  hasHint: boolean;
}

const FieldContext = createContext<FieldContextValue | null>(null);

/** Props a form control spreads to wire itself to the surrounding <Field>. */
export function useFieldControl(overrides: { id?: string; 'aria-describedby'?: string; 'aria-invalid'?: AriaAttributes['aria-invalid']; required?: boolean } = {}) {
  const ctx = useContext(FieldContext);
  const describedBy = [overrides['aria-describedby'], ctx?.hasHint ? ctx.hintId : undefined, ctx?.invalid ? ctx.errorId : undefined].filter(Boolean).join(' ');
  return {
    id: overrides.id ?? ctx?.id,
    'aria-describedby': describedBy || undefined,
    'aria-invalid': overrides['aria-invalid'] ?? (ctx?.invalid ? true : undefined),
    required: overrides.required ?? (ctx?.required || undefined),
    invalid: ctx?.invalid ?? false,
  };
}

export interface FieldProps {
  label: ReactNode;
  children: ReactNode;
  hint?: ReactNode;
  /** Error text. When set the field is invalid and the message is announced. */
  error?: ReactNode;
  required?: boolean;
  optional?: boolean;
  /** Visually hide the label (it stays for screen readers). */
  hideLabel?: boolean;
  className?: string;
  /** Right-aligned content on the label row (e.g. a "Forgot password?" link). */
  labelAside?: ReactNode;
}

/** Label + control + hint + error, wired with ids and aria-describedby. */
export function Field({ label, children, hint, error, required, optional, hideLabel, className, labelAside }: FieldProps) {
  const id = useId();
  const invalid = Boolean(error);
  const ctx: FieldContextValue = { id, hintId: `${id}-hint`, errorId: `${id}-error`, invalid, required: Boolean(required), hasHint: Boolean(hint) };
  return (
    <FieldContext.Provider value={ctx}>
      <div className={cn('flex min-w-0 flex-col gap-1.5', className)}>
        <div className={cn('flex items-baseline justify-between gap-2', hideLabel && 'sr-only')}>
          <label htmlFor={id} className="text-sm font-medium text-ink">
            {label}
            {required && (
              <span className="ml-0.5 text-danger-600 dark:text-danger-500" aria-hidden="true">
                *
              </span>
            )}
            {optional && <span className="ml-1.5 text-xs font-normal text-ink-subtle">Optional</span>}
          </label>
          {labelAside}
        </div>
        {children}
        {hint && !invalid && (
          <p id={ctx.hintId} className="text-xs text-ink-muted">
            {hint}
          </p>
        )}
        {invalid && (
          <p id={ctx.errorId} className="flex items-start gap-1.5 text-xs font-medium text-danger-700 dark:text-danger-500" role="alert">
            <AlertCircle className="mt-px h-3.5 w-3.5 shrink-0" aria-hidden="true" />
            <span>{error}</span>
          </p>
        )}
      </div>
    </FieldContext.Provider>
  );
}

/** Lays out Fields in a responsive grid. `cols` is the column count from `sm` up. */
export function FormRow({ children, cols = 2, className }: { children: ReactNode; cols?: 1 | 2 | 3 | 4; className?: string }) {
  const grid = { 1: 'sm:grid-cols-1', 2: 'sm:grid-cols-2', 3: 'sm:grid-cols-3', 4: 'sm:grid-cols-2 lg:grid-cols-4' } as const;
  return <div className={cn('grid grid-cols-1 gap-4', grid[cols], className)}>{children}</div>;
}

/** A titled group of fields with a description, used on long forms (patron, bibliographic record, settings). */
export function FormSection({ title, description, children, aside, className }: { title: string; description?: ReactNode; children: ReactNode; aside?: ReactNode; className?: string }) {
  const id = useId();
  return (
    <section aria-labelledby={id} className={cn('grid gap-5 border-t border-line py-6 first:border-t-0 first:pt-0 lg:grid-cols-[minmax(0,15rem)_minmax(0,1fr)] lg:gap-10', className)}>
      <div>
        <h3 id={id} className="font-display text-base font-semibold text-ink">
          {title}
        </h3>
        {description && <p className="mt-1 text-sm text-ink-muted">{description}</p>}
        {aside}
      </div>
      <div className="flex min-w-0 flex-col gap-4">{children}</div>
    </section>
  );
}
